import { useState, MouseEventHandler, useEffect } from 'react';
import PrintSettings from "./PrintSetting";
import { sendRequest } from '../../utils/api';
import FullPageSpinner from '../../components/FullPageSpinner';
import { useToast } from '../../context/ToastContext';
import {
    CURRENCY, LATE_CHECKOUT_AMOUNT, CAT_RATE,
    LATE_CHECKOUT_DURATION, SERVICE_CHARGE_RATE,
    SHORT_TIME_AMOUNT, SHORT_TIME_DURATION, VAT_RATE,
    EARLY_CHECKIN_AMOUNT
} from '../../constants';
import { formatDate } from '../../utils/date';
import { capitalizeFirst } from '../../utils/string';
import { initAllStores } from '../../utils/cache';

type ButtonType = 'primary' | 'danger' | 'outline';

type RenderButtonFn = (
    label: string,
    onClick?: MouseEventHandler<HTMLButtonElement> | null,
    type?: ButtonType
) => JSX.Element;

export default function SettingsPage(): JSX.Element {
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState<string>('business-info');

    const [loading, setLoading] = useState<boolean>(false);

    // ✅ Central data store for all sections
    const [data, setData] = useState({
        information: {
            businessName: "",
            website: "",
            email: "",
            phone: "",
            altPhone: "",
            address: "",
            logo: null as File | null,
        },
        preference: {
            vatRate: VAT_RATE,
            catRate: CAT_RATE,
            serviceCharge: SERVICE_CHARGE_RATE,
            shortTimeAmount: SHORT_TIME_AMOUNT,
            lateCheckoutAmount: LATE_CHECKOUT_AMOUNT,
            shortTimeDuration: SHORT_TIME_DURATION,
            lateCheckoutDuration: LATE_CHECKOUT_DURATION,
            earlyCheckinAmount: EARLY_CHECKIN_AMOUNT,
            currency: CURRENCY,
            offlineMode: true,
            chat: true,
            loan: true,
            leave: true,
        },
        subscription: {
            // plan: "Wiventory Hotel Pro",
            status: "",
            renewalDate: "",
            // subscriptionId: "SUB-23H12P9",
        },
        print: {},
    });

    const upsertSetting = async (section: keyof typeof data) => {
        setLoading(true);
        try {
            const cache = await initAllStores();
            await sendRequest("POST", `/hotels/settings/${section}`, data[section]);

            const hotel = await cache.getBy("data", { id: "hotel" });
            hotel[section] = data[section];
            await cache.updateManyBy("data", ["hotel"], hotel);
            showToast(`${capitalizeFirst(section)} settings saved successfully!`, { type: "success" });
        } catch (err) {
            console.log(err)
            showToast("Failed to update hotel setting. Try again.", { type: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const getSetting = async () => {
            setLoading(true);

            try {
                const response = await sendRequest("GET", "/hotels/settings");
                const data = response.data;
                const { information, preference, subscription, print } = data;

                // ✅ Map and format data to match your state shape
                setData({
                    information: {
                        businessName: information?.businessName,
                        website: information?.website ?? "",
                        email: information?.email,
                        phone: information?.phone,
                        altPhone: information?.altPhone ?? "",
                        address: information?.address,
                        logo: information?.logo || null,
                    },
                    preference: {
                        vatRate: preference?.vatRate ?? VAT_RATE,
                        catRate: preference?.catRate ?? CAT_RATE,
                        serviceCharge: preference?.serviceCharge ?? SERVICE_CHARGE_RATE,
                       earlyCheckinAmount: preference?.lateCheckoutAmount ?? LATE_CHECKOUT_AMOUNT,
                        shortTimeAmount: preference?.earlyCheckinAmount?? EARLY_CHECKIN_AMOUNT,
                        lateCheckoutAmount: preference?.lateCheckoutAmount ?? LATE_CHECKOUT_AMOUNT,
                        shortTimeDuration: preference?.shortTimeDuration ?? SHORT_TIME_DURATION,
                        lateCheckoutDuration: preference?.lateCheckoutDuration ?? LATE_CHECKOUT_DURATION,
                        currency: preference?.currency || CURRENCY,
                        offlineMode: preference?.offlineMode ?? true,
                        chat: preference?.chat ?? true,
                        loan: preference?.loan ?? true,
                        leave: preference?.leave ?? true,
                    },
                    subscription: {
                        status: subscription?.status,
                        renewalDate: subscription?.renewalDate,
                    },
                    print: print || {},
                });
            } catch (err) {
                console.error(err);
                showToast("Failed to load hotel settings. Try again.", { type: "error" });
            } finally {
                setLoading(false);
            }
        };

        getSetting();
    }, []);

    const renderButton: RenderButtonFn = (label, onClick, type = 'primary') => (
        <button
            onClick={onClick ?? undefined}
            className={`px-4 py-2 rounded text-white text-sm font-medium transition-colors duration-150 ${type === 'danger'
                ? 'bg-red-600 hover:bg-red-700'
                : type === 'outline'
                    ? 'bg-transparent border border-blue-600 text-blue-600 hover:bg-blue-50'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
        >
            {label}
        </button>
    );

    return (
        <div className="p-4 relative">
            <FullPageSpinner show={loading} />
            <h1 className="text-2xl font-bold mb-6">Business Settings</h1>

            {/* Fixed Tab Header */}
            <div className="sticky top-0 z-10 pb-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 border-b pb-3">
                    {[
                        { key: 'business-info', label: 'Business Info' },
                        { key: 'business-pref', label: 'Business Preferences' },
                        // { key: 'subscription', label: 'Subscription & Billing' },
                        { key: 'print', label: 'Print & Receipt' },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`w-full py-2 rounded text-sm font-medium border transition-colors duration-150 ${activeTab === tab.key
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="mt-6 overflow-y-auto max-h-[80vh] pr-2 space-y-8">

                {/* Business Info */}
                {activeTab === 'business-info' && (
                    <div className="border rounded-lg p-5 bg-white space-y-4">
                        <h2 className="text-lg font-semibold mb-4">Business Details</h2>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Business Name</label>
                                <input
                                    type="text"
                                    value={data.information.businessName}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, businessName: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            {/* <div>
                                <label className="block text-sm font-medium mb-1">Business Type</label>
                                <input
                                    type="text"
                                    value={data.information.businessType}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, businessType: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div> */}
                            <div>
                                <label className="block text-sm font-medium mb-1">Website</label>
                                <input
                                    type="text"
                                    value={data.information.website}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, website: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Phone</label>
                                <input
                                    type="text"
                                    value={data.information.phone}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, phone: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Alt Phone</label>
                                <input
                                    type="text"
                                    value={data.information.altPhone}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, altPhone: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Email</label>
                                <input
                                    type="email"
                                    value={data.information.email}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, email: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Address</label>
                                <input
                                    type="text"
                                    value={data.information.address}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, address: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Logo</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            information: { ...prev.information, logo: e.target.files?.[0] || null },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                        </div>
                        <div className="mt-4">
                            {renderButton('Save Changes', () => upsertSetting('information'))}
                        </div>
                    </div>
                )}

                {/* Business Preferences */}
                {activeTab === 'business-pref' && (
                    <div className="border rounded-lg p-5 bg-white space-y-4">
                        <h2 className="text-lg font-semibold mb-4">Business Preferences</h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">VAT Rate (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={data.preference.vatRate}
                                    onChange={(e) => {
                                        const value = (
                                            parseInt(e.target.value) >= 100
                                                ? "100" : e.target.value
                                        );
                                        setData((prev) => ({
                                            ...prev,
                                            preference: {
                                                ...prev.preference,
                                                vatRate: parseFloat(value)
                                            },
                                        }))
                                    }}
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">CAT Rate (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={data.preference.catRate}
                                    onChange={(e) => {
                                        const value = (
                                            parseInt(e.target.value) >= 100
                                                ? "100" : e.target.value
                                        );
                                        setData((prev) => ({
                                            ...prev,
                                            preference: {
                                                ...prev.preference,
                                                catRate: parseFloat(value)
                                            },
                                        }))
                                    }}
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Service Charge (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={data.preference.serviceCharge}
                                    onChange={(e) => {
                                        const value = (
                                            parseInt(e.target.value) >= 100
                                                ? "100" : e.target.value
                                        );
                                        setData((prev) => ({
                                            ...prev,
                                            preference: {
                                                ...prev.preference,
                                                serviceCharge: parseFloat(value)
                                            },
                                        }))
                                    }}
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>





                            <div>
                                <label className="block text-sm font-medium mb-1">Early Checkin Amount</label>
                                <input
                                    type="number"
                                    value={data.preference.earlyCheckinAmount}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, earlyCheckinAmount: parseFloat(e.target.value) },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>




                            <div>
                                <label className="block text-sm font-medium mb-1">Short Time Amount</label>
                                <input
                                    type="number"
                                    value={data.preference.shortTimeAmount}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, shortTimeAmount: parseFloat(e.target.value) },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Late Checkout Amount</label>
                                <input
                                    type="number"
                                    value={data.preference.lateCheckoutAmount}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, lateCheckoutAmount: parseFloat(e.target.value) },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>


                            <div>
                                <label className="block text-sm font-medium mb-1">Short Time Duration</label>
                                <input
                                    type="number"
                                    value={data.preference.shortTimeDuration}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, shortTimeDuration: parseFloat(e.target.value) },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Late Checkout Duration</label>
                                <input
                                    type="number"
                                    value={data.preference.lateCheckoutDuration}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, lateCheckoutDuration: parseFloat(e.target.value) },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Currency Type</label>
                                <select
                                    value={data.preference.currency}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, currency: e.target.value },
                                        }))
                                    }
                                    className="w-full border rounded px-3 py-2"
                                >
                                    <option value="NGN">₦ Nigerian Naira</option>
                                    <option value="USD">$ US Dollar</option>
                                    <option value="GBP">£ British Pound</option>
                                    <option value="EUR">€ Euro</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-6">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={data.preference.offlineMode}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, offlineMode: e.target.checked },
                                        }))
                                    }
                                />
                                Enable Offline Mode
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={data.preference.chat}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, chat: e.target.checked },
                                        }))
                                    }
                                />
                                Enable Chat
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={data.preference.loan}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, loan: e.target.checked },
                                        }))
                                    }
                                />
                                Enable Loan Applications
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={data.preference.leave}
                                    onChange={(e) =>
                                        setData((prev) => ({
                                            ...prev,
                                            preference: { ...prev.preference, leave: e.target.checked },
                                        }))
                                    }
                                />
                                Enable Leave Applications
                            </label>
                        </div>

                        <div className="mt-4">
                            {renderButton('Save Changes', () => upsertSetting('preference'))}
                        </div>
                    </div>
                )}

                {/* Subscription */}
                {activeTab === 'subscription' && (
                    <div className="border rounded-lg p-5 bg-white space-y-6">
                        {/* Status */}
                        {/* Status and Renewal */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-x-4">
                                <span className="font-medium text-gray-700 w-32">Status:</span>
                                <span
                                    className={`px-3 py-1 rounded-full text-sm font-semibold
                                        ${data.subscription.status?.toLowerCase() === 'active'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}
                                >
                                    {data.subscription.status || 'Inactive'}
                                </span>
                            </div>

                            <div className="flex items-center gap-x-4">
                                <span className="font-medium text-gray-700 w-32">Renewal Date:</span>
                                <span className="text-gray-800">
                                    {formatDate(data.subscription.renewalDate) || '-'}
                                </span>
                            </div>
                        </div>

                        {/* Subscribe Now Button */}
                        <div className="mt-4">
                            {renderButton('Subscribe Now')}
                        </div>

                        {/* Billing History */}
                        {/* <div className="mt-6">
                            <h3 className="text-md font-semibold mb-2">Billing History</h3>
                            <table className="w-full text-sm border">
                                <thead>
                                    <tr className="bg-gray-100 text-left">
                                        <th className="p-2 border">Date</th>
                                        <th className="p-2 border">Description</th>
                                        <th className="p-2 border">Amount</th>
                                        <th className="p-2 border">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="p-2 border">03 Oct 2025</td>
                                        <td className="p-2 border">Monthly Subscription</td>
                                        <td className="p-2 border">₦15,000</td>
                                        <td className="p-2 border text-green-600">Paid</td>
                                    </tr>
                                    <tr>
                                        <td className="p-2 border">03 Sep 2025</td>
                                        <td className="p-2 border">Monthly Subscription</td>
                                        <td className="p-2 border">₦15,000</td>
                                        <td className="p-2 border text-green-600">Paid</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div> */}

                    </div>
                )}

                {/* Print & Receipt */}
                {activeTab === 'print' && (
                    <div className="border rounded-lg p-5 bg-white">
                        <h2 className="text-lg font-semibold mb-4">Print & Receipt Settings</h2>
                        <PrintSettings
                            initialSettings={data.print} // pass print settings from main hotel data
                            onSave={async (updated) => {
                                // call your API to persist
                                try {
                                    setLoading(true);
                                    await sendRequest("POST", "/hotels/settings/print", updated);
                                    showToast("Print settings saved successfully!", { type: "success" });
                                    setData(prev => ({ ...prev, print: updated }));
                                } catch (err: any) {
                                    showToast("Failed to save print settings", { type: "error" })
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        />

                        {/* <div className="mt-4">
                            {renderButton('Save Changes', () => upsertSetting('print'))}
                        </div> */}
                    </div>
                )}
            </div>
        </div>
    );
}
